/**
 * Forms Settings — the Form link token and the ONE message validator both new tabs (New Patients, Medical History Update) use. Pure.
 * Plan: docs/plans/2026-10-20-forms-settings-plan.md §0 (D6, D15), §1f (extra validation), §2.4, §2.5.
 *
 * ⛔ KEEP THIS FOLDER FREE OF OUTSIDE IMPORTS, with NAMED exports only (no `export default`). The three files here
 *    (formLink.js, newPatient.js, medHx.js) import ONLY each other — one-line `import { a, b } from './formLink';` — and
 *    nothing from vue, the store, lodash or src/utils. cypress/node-tests/forms-settings-rules.test.js executes them
 *    (sibling imports stripped, files concatenated into one scope), and so do the twin tests in forms-service and teamio-admin.
 *    Because they share ONE scope there, every top-level name across the three files must be UNIQUE.
 *
 * ⛔ THE SERVER IS THE GATE. forms-service lib/formsSettings/rules.js is the same rule, same codes, same sentences, same order;
 *    it refuses a bad save with 400 and the sender re-checks. This file is the hint the editor shows while typing.
 *    forms-service tests/forms-settings-rules.test.js pins the two against drift (it FAILS, never skips, when this checkout is missing).
 *
 * ⛔ ONE LINK PER MESSAGE. The token is `{{form_link}}`; the chosen form / packet is stored BESIDE the text as
 *    `formLink: { kind: 'form' | 'packet', id, name }`. forms-service mints the patient-specific short URL at send time.
 *
 * ⛔ THE ONLY VARIABLES THE SENDER FILLS are ALLOWED_VARIABLES. Anything else inside {{ }} would be texted literally,
 *    so it is refused here (UNKNOWN_VARIABLE). There is no Email-signature chip: the email layout appends the signature.
 */

/** The one required token in every message that is on. */
export const FORM_LINK_TOKEN = '{{form_link}}';

/** A 33-character stand-in for the minted short link — what the SMS counter assumes. */
export const FORM_LINK_SAMPLE_URL = 'https://prod.teamio.app/f/xxxxxxx';

/** What the live preview shows where the link goes (seven bullets: the real code is not known until send time). */
export const FORM_LINK_PREVIEW_URL = 'https://prod.teamio.app/f/•••••••';

/** The variable chips under a message box, in order. The Form link chip is separate in the UI; there is no signature chip. */
export const MESSAGE_CHIPS = Object.freeze([
  Object.freeze({ label: 'First name', token: '{{patient_first_name}}' }),
  Object.freeze({ label: 'Last name', token: '{{patient_last_name}}' }),
  Object.freeze({ label: 'Practice name', token: '{{site_name}}' }),
  Object.freeze({ label: 'Office phone', token: '{{site_officephone}}' }),
  Object.freeze({ label: 'Appointment date', token: '{{apt_date}}' }),
]);

/** Every `{{name}}` the sender fills: the five chips plus form_link. Anything else is refused. */
export const ALLOWED_VARIABLES = Object.freeze(
  MESSAGE_CHIPS.map((chip) => chip.token.replace(/^\{\{|\}\}$/g, '')).concat(['form_link']),
);

export const MAX_SMS_BODY_CHARS = 1600;
export const MAX_EMAIL_SUBJECT_CHARS = 200;
export const MAX_EMAIL_BODY_CHARS = 20000;

/** The fixed sentences of validateMessage (UNKNOWN_VARIABLE and TOO_LONG name the variable / the limit, so they are built below). */
export const MESSAGE_ERROR_TEXT = Object.freeze({
  BODY_BLANK: 'This message is required. Reset to default or write your own.',
  SUBJECT_BLANK: 'A subject line is required.',
  LINK_MISSING: 'Add the Form link so patients can open their forms.',
  LINK_DUPLICATE: 'Use the Form link only once in a message.',
  LINK_NOT_PICKED: 'Choose which form this link opens.',
  LINK_IN_SUBJECT: 'The Form link goes in the message, not the subject.',
});

/**
 * The switch card's reasons that both sending tabs share, word for word. The first reason shown is the first that applies;
 * the order lives in newPatientBlockReasons / medHxBlockReasons.
 */
export const SHARED_BLOCK_MESSAGES = Object.freeze({
  MESSAGE_PROBLEMS: 'Add a form link to every message that is on',
  SMS_NOT_READY: 'Text needs a texting number. Turn Text off, or set one up in Settings → Text.',
  EMAIL_NOT_READY: 'Email needs a verified sender (Settings → Email).',
  NOTHING_ON: 'Turn on Text or Email first.',
});

/** The tolerant boolean reader: the 'TRUE' string (what we write) or a JSON true (what a stray writer might). */
export function isOn(v) {
  return v === 'TRUE' || v === true;
}

export function isPlainObject(v) {
  return !!v && typeof v === 'object' && !Array.isArray(v);
}

/** A string with something other than whitespace in it. */
export function isFilledText(v) {
  return typeof v === 'string' && v.trim() !== '';
}

/** How many `{{form_link}}` tokens the text holds. Tolerant of inner whitespace (`{{ form_link }}`). */
export function countFormLinks(text) {
  const found = String(text === null || text === undefined ? '' : text).match(/\{\{\s*form_link\s*\}\}/g);
  return found ? found.length : 0;
}

/** `{ kind: 'form' | 'packet', id: non-blank string, name: string }` — the choice stored beside the text. */
export function isValidFormLink(v) {
  return isPlainObject(v)
    && (v.kind === 'form' || v.kind === 'packet')
    && isFilledText(v.id)
    && typeof v.name === 'string';
}

/**
 * The distinct `{{name}}`s in a text that the sender would not fill, in order of appearance (inner whitespace collapsed).
 * `skip` names a variable that is allowed in the BODY but handled separately where this is called (form_link in the subject,
 * which validateMessage reports as LINK_IN_SUBJECT instead).
 */
function unknownVariablesIn(text, skip) {
  const found = [];
  String(text === null || text === undefined ? '' : text).replace(/\{\{\s*([^{}]*?)\s*\}\}/g, (whole, rawName) => {
    const name = rawName.replace(/\s+/g, ' ');
    if (name !== skip && ALLOWED_VARIABLES.indexOf(name) === -1 && found.indexOf(name) === -1) found.push(name);
    return whole;
  });
  return found;
}

/**
 * One message → the problems to show.
 *   msg     { on: 'TRUE'|'FALSE', body, subject?, formLink }
 *   channel 'sms' | 'email'   (the subject only exists on email)
 * Returns [{ field: 'body'|'subject'|'formLink', code, message }] — [] when the message is not ON (a message that is off is never
 * validated, so an office can leave a half-written box alone). Order: subject problems (LINK_IN_SUBJECT, unknown variables, too long),
 * then body problems, then the link choice. A `{{form_link}}` in an email SUBJECT is refused (LINK_IN_SUBJECT, once, whatever the body
 * holds) and is not also reported as UNKNOWN_VARIABLE; in the body it is the one required token.
 */
export function validateMessage(msg, channel) {
  const m = isPlainObject(msg) ? msg : {};
  if (!isOn(m.on)) return [];
  const email = channel === 'email';
  const problems = [];
  const add = (field, code, message) => problems.push({ field, code, message });

  if (email) {
    if (!isFilledText(m.subject)) {
      add('subject', 'SUBJECT_BLANK', MESSAGE_ERROR_TEXT.SUBJECT_BLANK);
    } else {
      if (countFormLinks(m.subject) > 0) add('subject', 'LINK_IN_SUBJECT', MESSAGE_ERROR_TEXT.LINK_IN_SUBJECT);
      unknownVariablesIn(m.subject, 'form_link').forEach((name) => add('subject', 'UNKNOWN_VARIABLE', `{{${name}}} isn't something we can fill in.`));
      if (m.subject.length > MAX_EMAIL_SUBJECT_CHARS) {
        add('subject', 'TOO_LONG', `The email subject is too long (max ${MAX_EMAIL_SUBJECT_CHARS} characters).`);
      }
    }
  }

  if (!isFilledText(m.body)) {
    add('body', 'BODY_BLANK', MESSAGE_ERROR_TEXT.BODY_BLANK);
  } else {
    const links = countFormLinks(m.body);
    if (links === 0) add('body', 'LINK_MISSING', MESSAGE_ERROR_TEXT.LINK_MISSING);
    else if (links > 1) add('body', 'LINK_DUPLICATE', MESSAGE_ERROR_TEXT.LINK_DUPLICATE);
    if (links > 0 && !isValidFormLink(m.formLink)) add('formLink', 'LINK_NOT_PICKED', MESSAGE_ERROR_TEXT.LINK_NOT_PICKED);
    unknownVariablesIn(m.body).forEach((name) => add('body', 'UNKNOWN_VARIABLE', `{{${name}}} isn't something we can fill in.`));
    if (email && m.body.length > MAX_EMAIL_BODY_CHARS) {
      add('body', 'TOO_LONG', `The email message is too long (max ${MAX_EMAIL_BODY_CHARS} characters).`);
    } else if (!email && m.body.length > MAX_SMS_BODY_CHARS) {
      add('body', 'TOO_LONG', `The text message is too long (max ${MAX_SMS_BODY_CHARS} characters).`);
    }
  }
  return problems;
}
